import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  ClipboardList, ListChecks, Thermometer, PackageCheck, AlertTriangle,
  FolderArchive, Building2, ThermometerSnowflake, History, FileDown, Flame,
  GraduationCap, CalendarCog, LogOut, Menu, X,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  {
    label: "Dashboard",
    items: [
      { to: "/", label: "Todo-Liste", icon: ClipboardList, testid: "sidebar-nav-todo", end: true },
      { to: "/protokolle", label: "Protokolle", icon: ListChecks, testid: "sidebar-nav-protokolle" },
    ],
  },
  {
    label: "Dokumentation",
    items: [
      { to: "/erhitzung", label: "Erhitzungsprotokoll", icon: Flame, testid: "sidebar-nav-erhitzung" },
      { to: "/wareneingang", label: "Wareneingang", icon: PackageCheck, testid: "sidebar-nav-wareneingang" },
      { to: "/vorfaelle", label: "Vorfälle & Maßnahmen", icon: AlertTriangle, testid: "sidebar-nav-vorfaelle" },
      { to: "/schulungen", label: "Schulungen", icon: GraduationCap, testid: "sidebar-nav-schulungen" },
      { to: "/dokumente", label: "Dokumentenablage", icon: FolderArchive, testid: "sidebar-nav-dokumente" },
    ],
  },
  {
    label: "Verwaltung",
    items: [
      { to: "/temperatur", label: "Temperaturkontrolle", icon: ThermometerSnowflake, testid: "sidebar-nav-temperatur" },
      { to: "/raeume", label: "Räume & Objekte", icon: Building2, testid: "sidebar-nav-raeume" },
      { to: "/einstellungen", label: "Öffnungszeiten", icon: CalendarCog, testid: "sidebar-nav-einstellungen" },
    ],
  },
  {
    label: "Berichte",
    items: [
      { to: "/export", label: "PDF-Export", icon: FileDown, testid: "sidebar-nav-export" },
    ],
  },
];

function NavContent({ onNavigate }) {
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-3 px-5 py-5 border-b border-slate-200">
        <img src="/haccp-logo.png" alt="HACCP" className="h-11 w-11 rounded-xl object-contain" />
        <div className="leading-tight">
          <div className="font-heading text-base font-bold text-slate-900">HACCP-Doku</div>
          <div className="text-xs text-slate-500">Bowlingcenter Eisleben</div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
        {SECTIONS.map((sec) => (
          <div key={sec.label}>
            <div className="px-3 mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              {sec.label}
            </div>
            <div className="space-y-0.5">
              {sec.items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={it.testid}
                  onClick={onNavigate}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-teal-50 text-teal-700"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`
                  }
                >
                  <it.icon className="h-[18px] w-[18px]" />
                  {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-slate-200 p-3">
        <div className="rounded-lg bg-slate-50 px-3 py-2.5 mb-2 text-[11px] leading-snug text-slate-500">
          <div className="font-semibold text-slate-700">{user?.name || "Admin"}</div>
          Friedensstraße 12, 06295 Eisleben<br />
          Ruhetag: Dienstag
        </div>
        <button
          data-testid="btn-logout"
          onClick={() => { logout(); navigate("/login"); }}
          className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Abmelden
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-72 flex-col bg-white border-r border-slate-200 z-30">
        <NavContent />
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <img src="/haccp-logo.png" alt="HACCP" className="h-8 w-8 object-contain" />
          <span className="font-heading font-bold text-slate-900">HACCP-Doku</span>
        </div>
        <button data-testid="btn-mobile-menu" onClick={() => setOpen(true)} className="p-2 text-slate-700">
          <Menu className="h-6 w-6" />
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 p-1 text-slate-500 z-10">
              <X className="h-5 w-5" />
            </button>
            <NavContent onNavigate={() => setOpen(false)} />
          </div>
        </div>
      )}

      <main className="lg:pl-72">
        <div className="mx-auto max-w-6xl p-4 sm:p-6 lg:p-8">{children}</div>
      </main>
    </div>
  );
}
